import { Link } from 'react-router'
import { Eye, EyeOff, Loader2, ArrowLeft, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useRegisterForm } from './RegisterForm.logic'

export function RegisterForm() {
  const {
    t,
    form,
    currentStep,
    isLoading,
    showPassword,
    showConfirmPassword,
    onSubmit,
    handleNext,
    handleBack,
    toggleShowPassword,
    toggleShowConfirmPassword,
    REGISTER_STEPS,
  } = useRegisterForm()

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-6 flex items-center justify-center gap-3">
          <div
            className={`flex size-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
              currentStep === REGISTER_STEPS.PERSONAL
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            1
          </div>
          <div className="h-px w-8 bg-border" />
          <div
            className={`flex size-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
              currentStep === REGISTER_STEPS.FACILITY
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            2
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {currentStep === REGISTER_STEPS.PERSONAL && (
              <>
                <h3 className="text-lg font-medium">{t('personalInfo')}</h3>

                <div className="grid grid-cols-2 items-start gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('firstName')}</FormLabel>
                        <FormControl>
                          <Input autoComplete="given-name" disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('lastName')}</FormLabel>
                        <FormControl>
                          <Input autoComplete="family-name" disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="name@example.com"
                          autoComplete="email"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('phone')}</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="+359888999888"
                          autoComplete="tel"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('password')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            disabled={isLoading}
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={toggleShowPassword}
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <EyeOff className="size-4 text-muted-foreground" />
                            ) : (
                              <Eye className="size-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('confirmPassword')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            disabled={isLoading}
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={toggleShowConfirmPassword}
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="size-4 text-muted-foreground" />
                            ) : (
                              <Eye className="size-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="privacyAccepted"
                  render={({ field }) => (
                    <FormItem className="flex items-start gap-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <div className="space-y-1">
                        <FormLabel className="cursor-pointer font-normal leading-snug">
                          {t('privacyAccepted')}{' '}
                          <Link
                            to="/privacy-policy"
                            target="_blank"
                            className="text-primary hover:underline"
                          >
                            {t('privacyPolicy')}
                          </Link>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                <Button type="button" className="w-full" onClick={handleNext}>
                  {t('next')}
                  <ArrowRight className="size-4" />
                </Button>
              </>
            )}

            {currentStep === REGISTER_STEPS.FACILITY && (
              <>
                <h3 className="text-lg font-medium">{t('facilityInfo')}</h3>

                <FormField
                  control={form.control}
                  name="facility.name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('facilityName')}</FormLabel>
                      <FormControl>
                        <Input disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="facility.address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('facilityAddress')}</FormLabel>
                      <FormControl>
                        <Input disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 items-start gap-4">
                  <FormField
                    control={form.control}
                    name="facility.city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('facilityCity')}</FormLabel>
                        <FormControl>
                          <Input disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="facility.postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('facilityPostalCode')}</FormLabel>
                        <FormControl>
                          <Input disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 items-start gap-4">
                  <FormField
                    control={form.control}
                    name="facility.phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('facilityPhone')}</FormLabel>
                        <FormControl>
                          <Input type="tel" disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="facility.bedCount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('facilityBedCount')}</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            disabled={isLoading}
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="facility.email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('facilityEmail')}</FormLabel>
                      <FormControl>
                        <Input type="email" disabled={isLoading} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 items-start gap-4">
                  <FormField
                    control={form.control}
                    name="facility.bulstat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('facilityBulstat')}</FormLabel>
                        <FormControl>
                          <Input disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="facility.registrationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('facilityRegistrationNumber')}</FormLabel>
                        <FormControl>
                          <Input disabled={isLoading} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {form.formState.errors.root && (
                  <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
                )}

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleBack}
                    disabled={isLoading}
                  >
                    <ArrowLeft className="size-4" />
                    {t('back')}
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isLoading}>
                    {isLoading && <Loader2 className="animate-spin" />}
                    {t('registerButton')}
                  </Button>
                </div>
              </>
            )}
          </form>
        </Form>
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          {t('hasAccount')}{' '}
          <Link to="/login" className="text-primary hover:underline">
            {t('login')}
          </Link>
        </p>
      </CardFooter>
    </Card>
  )
}
